<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { ATTACHMENT_TYPES } from '../constants';

const emit = defineEmits(['error']);

const { t } = useI18n();

const hasError = ref(false);
const showGallery = ref(false);
const { filteredCurrentChatAttachments, attachments, messageType } =
  useMessageContext();
const store = useStore();
const { isAdmin } = useAdmin();

const accountId = computed(() => store.getters.getCurrentAccountId);
const imageModeration = ref({});

const handleError = () => {
  hasError.value = true;
  emit('error');
};

const attachment = computed(() => {
  return attachments.value[0];
});

const isReel = computed(() => {
  return attachment.value.fileType === ATTACHMENT_TYPES.IG_REEL;
});

const isBlocked = computed(
  () =>
    imageModeration.value?.blocked === true &&
    imageModeration.value?.approved !== true
);

const isKeptBlocked = computed(
  () =>
    imageModeration.value?.review_decision === 'keep_blocked' ||
    imageModeration.value?.reviewDecision === 'keep_blocked'
);

const canBlockImage = computed(() => messageType.value === 0);

const moderationCategory = computed(() => {
  const category = imageModeration.value?.category;
  const knownCategories = [
    'pending',
    'nudity',
    'sexual',
    'violence',
    'self_harm',
    'hate',
    'illegal',
    'other_sensitive',
  ];
  // Categories are restricted to the fixed allowlist above, with an unknown fallback.
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(
    `CONVERSATION.REPLYBOX.CONTENT_SAFETY_CATEGORIES.${knownCategories.includes(category) ? category : 'unknown'}`
  );
});

watch(
  attachment,
  value => {
    imageModeration.value =
      value?.imageModeration || value?.image_moderation || {};
  },
  { immediate: true }
);

const announce = (message, priority = 'polite') => {
  window.dispatchEvent(
    new CustomEvent('a11y-announce', { detail: { message, priority } })
  );
};

const reviewImage = async decision => {
  try {
    const { data } = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/lumerai_alerts/review_attachment`,
      { attachment_id: attachment.value.id, decision }
    );
    imageModeration.value = data.moderation;
    let successMessage = t('CONVERSATION.REPLYBOX.KEEP_IMAGE_HIDDEN_SUCCESS');
    if (decision === 'approve') {
      successMessage = t('CONVERSATION.REPLYBOX.AUTHORIZE_IMAGE_SUCCESS');
    } else if (data.awaiting_admin_review) {
      successMessage = t('CONVERSATION.REPLYBOX.IMAGE_AWAITING_ADMIN');
    }
    useAlert(successMessage);
    announce(successMessage, 'assertive');
  } catch (error) {
    const message =
      error.response?.data?.error ||
      t('CONVERSATION.REPLYBOX.CONTENT_SAFETY_REVIEW_ERROR');
    useAlert(message);
    announce(message, 'assertive');
  }
};
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="video"
    @click="!isBlocked && (showGallery = true)"
  >
    <div
      v-if="isBlocked"
      class="flex max-w-md flex-col gap-3 rounded-lg border border-n-ruby-7 bg-n-ruby-2 p-4 text-n-slate-12"
      @click.stop
    >
      <div class="flex items-start gap-3">
        <Icon
          aria-hidden="true"
          icon="i-lucide-shield-alert"
          class="mt-0.5 size-5 flex-shrink-0 text-n-ruby-10"
        />
        <div>
          <p class="mb-1 font-semibold">
            {{ t('CONVERSATION.REPLYBOX.CONTENT_SAFETY_BLOCKED') }}
          </p>
          <p class="mb-0 text-sm text-n-slate-11">
            {{
              t('CONVERSATION.REPLYBOX.CONTENT_SAFETY_CATEGORY', {
                category: moderationCategory,
                reason:
                  imageModeration.reason ||
                  t('CONVERSATION.REPLYBOX.VIDEO_BLOCKED_REASON'),
              })
            }}
          </p>
        </div>
      </div>
      <div class="flex flex-wrap gap-2">
        <Button
          v-if="!isKeptBlocked"
          sm
          solid
          ruby
          :label="t('CONVERSATION.REPLYBOX.KEEP_IMAGE_HIDDEN')"
          @click.stop="reviewImage('keep_blocked')"
        />
        <Button
          v-if="isAdmin"
          sm
          outline
          slate
          :label="t('CONVERSATION.REPLYBOX.AUTHORIZE_IMAGE')"
          @click.stop="reviewImage('approve')"
        />
      </div>
      <p v-if="isKeptBlocked" class="mb-0 text-xs text-n-slate-11">
        {{
          isAdmin
            ? t('CONVERSATION.REPLYBOX.IMAGE_BLOCK_CONFIRMED')
            : t('CONVERSATION.REPLYBOX.IMAGE_AWAITING_ADMIN')
        }}
      </p>
      <p v-if="!isAdmin" class="mb-0 text-xs text-n-slate-11">
        {{ t('CONVERSATION.REPLYBOX.IMAGE_ADMIN_UNLOCK_ONLY') }}
      </p>
    </div>
    <div v-else class="relative group rounded-lg overflow-hidden">
      <div
        v-if="isReel"
        class="absolute p-2 flex items-start justify-end right-0 pointer-events-none"
      >
        <Icon icon="i-lucide-instagram" class="text-white shadow-lg" />
      </div>
      <video
        controls
        class="rounded-lg skip-context-menu"
        :src="attachment.dataUrl"
        :class="{
          'max-w-48': isReel,
          'max-w-full': !isReel,
        }"
        @click.stop
        @error="handleError"
      />
    </div>
    <div
      v-if="!isBlocked && canBlockImage"
      class="mt-2 flex flex-col items-start gap-2"
      @click.stop
    >
      <Button
        sm
        outline
        ruby
        icon="i-lucide-shield-alert"
        :label="$t('CONVERSATION.REPLYBOX.BLOCK_IMAGE')"
        @click.stop="reviewImage('keep_blocked')"
      />
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>
